import { createMemo, For, Show, type VoidComponent } from "solid-js";
import { A } from "@solidjs/router";
import { keyFromSelector } from "i18next";
import { isNil } from "lodash-es";
import { useCustomerState } from "../../state/customer";
import { useTranslation, type TranslationKey } from "../../state/i18n";
import UserService from "../../services/UserService";
import type { Role } from "../../state/role";
import type { Grant } from "../../state/grant";
import type { User } from "../../state/user";
import type { Group } from "../../state/group";
import {
  canManageAllForms,
  filterMixedRolesForScope,
  getAccountRole,
  getRoleDescription,
  getRoleName,
  groupRoles,
  toGroupedRoleOptions,
  toRoleByIdMap,
} from "../../utils/roleUtil";
import { isInvite, resolveUserOrInviteName } from "../../utils/userUtil";
import { resolveName as resolveGroupName } from "../../utils/groupUtil";
import { compareByLocale } from "../../utils/stringUtil";
import type { Invite } from "../../state/invite";
import { IconButton } from "../../components/button/Button";
import type RoleId from "../../state/RoleId";
import type FolderId from "../../state/FolderId";
import type FormId from "../../state/FormId";
import type { MoreAppError } from "../../MoreAppError";
import { useModalManager } from "../../components/modal/ModalManager";
import GenericFailureModal from "../GenericFailureModal";
import { PillSingleSelect } from "../../components/menu/Select";
import PillSingleSelectTrigger from "../../components/menu/triggers/PillSingleSelectTrigger";
import type { SelectOption } from "../../types/Menu";
import { MemberIdentityCell } from "./MemberIdentityCell";

type ResourcePermissionsTranslations = {
  membersColumnTitle: TranslationKey;
  accountRoleColumnTitle: TranslationKey;
  resourceRoleColumnTitle: TranslationKey;
  manageAllLabel: TranslationKey;
  deletePermissionAriaLabel: TranslationKey;
};

const translations: Record<"FOLDER" | "FORM", ResourcePermissionsTranslations> = {
  FOLDER: {
    membersColumnTitle: keyFromSelector(($) => $.FOLDER_PAGE_PERMISSIONS_TAB_MEMBERS_TITLE),
    accountRoleColumnTitle: keyFromSelector(($) => $.FOLDER_PAGE_PERMISSIONS_TAB_ACCOUNT_ROLE_TITLE),
    resourceRoleColumnTitle: keyFromSelector(($) => $.FOLDER_PAGE_PERMISSIONS_TAB_FOLDER_ROLE_TITLE),
    manageAllLabel: keyFromSelector(($) => $.FOLDER_PAGE_PERMISSIONS_MANAGE_ALL_LABEL),
    deletePermissionAriaLabel: keyFromSelector(($) => $.FOLDER_PAGE_PERMISSIONS_DELETE_PERMISSION_ARIA_LABEL),
  },
  FORM: {
    membersColumnTitle: keyFromSelector(($) => $.FORM_PAGE_PERMISSIONS_TAB_MEMBERS_TITLE),
    accountRoleColumnTitle: keyFromSelector(($) => $.FORM_PAGE_PERMISSIONS_TAB_ACCOUNT_ROLE_TITLE),
    resourceRoleColumnTitle: keyFromSelector(($) => $.FORM_PAGE_PERMISSIONS_TAB_FORM_ROLE_TITLE),
    manageAllLabel: keyFromSelector(($) => $.FORM_PAGE_PERMISSIONS_MANAGE_ALL_LABEL),
    deletePermissionAriaLabel: keyFromSelector(($) => $.FORM_PAGE_PERMISSIONS_DELETE_PERMISSION_ARIA_LABEL),
  },
};

type MemberBase = {
  name: string;
  accountRole?: Role;
  resourceRole?: Role;
  resourceRoleId?: RoleId;
  hasManageAll: boolean;
};

type Member =
  | (MemberBase & { kind: "user"; userOrInvite: User | Invite })
  | (MemberBase & { kind: "group"; group: Group });

type MemberRowProps = {
  member: Member;
  resourceRoleOptions: SelectOption<RoleId>[];
  deletePermissionAriaLabel: TranslationKey;
  manageAllLabel: TranslationKey;
  disabled: boolean;
  onSelectRole: (roleId: RoleId | null) => void;
};

const MemberRow: VoidComponent<MemberRowProps> = (props) => {
  const { t } = useTranslation();
  const customer = useCustomerState();

  const roleOptions = createMemo<SelectOption<RoleId>[]>(() => {
    const { resourceRole, resourceRoleId } = props.member;

    const options = props.resourceRoleOptions;
    if (isNil(resourceRole) || isNil(resourceRoleId) || options.some((option) => option.value.equals(resourceRoleId))) {
      return options;
    }
    return [
      ...options,
      {
        variant: "text",
        label: getRoleName(resourceRole, t),
        value: resourceRoleId,
        description: getRoleDescription(resourceRole, t),
        ...(resourceRole.system ? {} : { group: t(($) => $.ADD_PERMISSION_MODAL_CUSTOM_ROLE_LABEL) }),
      },
    ];
  });

  const getMemberLink = (member: Member): string => {
    const path = member.kind === "user" ? `users/${member.userOrInvite.id}` : `groups/${member.group.id}`;
    return `/customers/${customer.customerId}/${path}`;
  };

  return (
    <div class="relative">
      <A
        href={getMemberLink(props.member)}
        class="absolute inset-0 rounded-sm -outline-offset-1 hover:bg-surface-hover focus-visible:outline-default"
      />
      <div
        role="row"
        class="pointer-events-none relative grid grid-cols-template items-center gap-6 text-base-regular text-secondary"
      >
        <MemberIdentityCell member={props.member} />
        <div role="cell" class="flex min-w-0 flex-wrap gap-1 py-4">
          <Show when={props.member.accountRole}>
            {(accountRole) => <span class="min-w-0 wrap-break-word">{getRoleName(accountRole(), t)}</span>}
          </Show>
        </div>
        <div role="cell" class="flex min-w-0 flex-wrap gap-1 py-4">
          <Show
            when={!props.member.hasManageAll}
            fallback={
              <PillSingleSelectTrigger
                ariaLabel={t(($) => $.USER_PAGE_RESOURCE_ROLE_ARIA, { name: props.member.name })}
                value={{ variant: "text", label: t(props.manageAllLabel), value: null }}
                disabled
              />
            }
          >
            <Show when={!isNil(props.member.resourceRole)}>
              <PillSingleSelect
                ariaLabel={t(($) => $.USER_PAGE_RESOURCE_ROLE_ARIA, { name: props.member.name })}
                options={roleOptions()}
                value={props.member.resourceRoleId}
                onSelect={(roleId) => props.onSelectRole(roleId)}
                disabled={props.disabled}
                class="pointer-events-auto"
              />
            </Show>
          </Show>
        </div>
        <Show when={!props.member.hasManageAll}>
          <div role="cell">
            <IconButton
              class="pointer-events-auto"
              icon="trash"
              aria-label={t(props.deletePermissionAriaLabel)}
              variant="tertiary"
              size="medium"
              disabled={props.disabled}
              onClick={() => props.onSelectRole(null)}
            />
          </div>
        </Show>
      </div>
    </div>
  );
};

type ResourcePermissionsContentProps =
  | { resourceType: "FOLDER"; resourceId: FolderId }
  | { resourceType: "FORM"; resourceId: FormId };

const ResourcePermissionsContent: VoidComponent<ResourcePermissionsContentProps> = (props) => {
  const { t } = useTranslation();
  const resourceTranslations = () => translations[props.resourceType];
  const customer = useCustomerState();
  const { open } = useModalManager();

  const updateGrantMutation = UserService.updateGrant(customer);
  const updateGroupGrantMutation = UserService.updateGroupGrant(customer);
  const openErrorModal = (err: MoreAppError) => open({ content: () => <GenericFailureModal error={err} /> });

  const usersState = UserService.getUsers(customer.customerId);
  const groupsState = UserService.getGroups(customer.customerId);
  const inviteState = UserService.getInvites(customer.customerId);
  const rolesState = UserService.getRoles(customer.customerId);

  const grantResource = () =>
    props.resourceType === "FORM"
      ? ({ resourceType: "FORM", resourceId: props.resourceId } as const)
      : ({ resourceType: "FOLDER", resourceId: props.resourceId } as const);

  const resolvedState = createMemo(() => {
    if (isNil(usersState.data) || isNil(groupsState.data) || isNil(inviteState.data) || isNil(rolesState.data)) {
      return;
    }
    return {
      users: usersState.data,
      groups: groupsState.data,
      invites: inviteState.data,
      roles: rolesState.data,
    };
  });

  const resourceRoleOptions = createMemo<SelectOption<RoleId>[]>(() => {
    const resolved = resolvedState();
    if (isNil(resolved)) {
      return [];
    }
    const grouped = groupRoles(resolved.roles);
    const scopeRoles = props.resourceType === "FORM" ? grouped.formRoles : grouped.folderRoles;
    const roles = [...scopeRoles, ...filterMixedRolesForScope(resolved.roles, props.resourceType)];
    return toGroupedRoleOptions(
      roles,
      t,
      t(($) => $.ADD_PERMISSION_MODAL_CUSTOM_ROLE_LABEL),
    );
  });

  const isRowPending = (member: Member) =>
    member.kind === "user"
      ? updateGrantMutation.isPending &&
        updateGrantMutation.variables?.userOrInviteId.toString() === member.userOrInvite.id.toString()
      : updateGroupGrantMutation.isPending &&
        updateGroupGrantMutation.variables?.groupId.toString() === member.group.id.toString();

  const selectRole = (member: Member, roleId: RoleId | null) => {
    if (member.kind === "user") {
      updateGrantMutation.mutate(
        {
          ...grantResource(),
          userOrInviteId: member.userOrInvite.id,
          isInvited: isInvite(member.userOrInvite),
          roleId,
          currentRoleId: member.resourceRoleId ?? null,
        },
        { onError: openErrorModal },
      );
    } else {
      updateGroupGrantMutation.mutate(
        {
          ...grantResource(),
          groupId: member.group.id,
          roleId,
          currentRoleId: member.resourceRoleId ?? null,
        },
        { onError: openErrorModal },
      );
    }
  };

  const members = createMemo<Member[]>(() => {
    const resolved = resolvedState();
    if (isNil(resolved)) {
      return [];
    }
    const roleById = toRoleByIdMap(resolved.roles);
    const grouped = groupRoles(resolved.roles);
    const resourceId = props.resourceId.toString();

    const resourceGrant = (grants: Grant[]) =>
      grants.find((grant) => grant.resourceType === props.resourceType && grant.resourceId.toString() === resourceId);

    const manageAllGroupIds = new Set(
      resolved.groups
        .filter((group) => canManageAllForms(group.grants, customer.customerId, roleById))
        .map((group) => group.id.toString()),
    );

    const userRows = [...resolved.users, ...resolved.invites]
      .map((user): Member | undefined => {
        const hasManageAll =
          canManageAllForms(user.grants, customer.customerId, roleById) ||
          user.groups.some((groupId) => manageAllGroupIds.has(groupId.toString()));
        const grant = resourceGrant(user.grants);
        if (isNil(grant) && !hasManageAll) {
          return;
        }
        return {
          kind: "user",
          userOrInvite: user,
          name: resolveUserOrInviteName(isInvite(user), user),
          accountRole: getAccountRole(user.grants, grouped),
          resourceRole: isNil(grant) ? undefined : roleById.get(grant.roleId.toString()),
          resourceRoleId: grant?.roleId,
          hasManageAll,
        };
      })
      .filter((row): row is Member => !isNil(row));

    const groupRows = resolved.groups
      .map((group): Member | undefined => {
        const hasManageAll = manageAllGroupIds.has(group.id.toString());
        const grant = resourceGrant(group.grants);
        if (isNil(grant) && !hasManageAll) {
          return;
        }
        return {
          kind: "group",
          group,
          name: resolveGroupName(group),
          accountRole: getAccountRole(group.grants, grouped),
          resourceRole: isNil(grant) ? undefined : roleById.get(grant.roleId.toString()),
          resourceRoleId: grant?.roleId,
          hasManageAll,
        };
      })
      .filter((row): row is Member => !isNil(row));

    return [...userRows, ...groupRows].sort((a, b) => compareByLocale(a.name, b.name));
  });

  return (
    <div class="py-6">
      <Show when={resolvedState()}>
        <div
          role="table"
          style={{
            "--column-template": "1fr 11.5rem 11.5rem 2.375rem",
          }}
          class="flex min-h-0 flex-1 flex-col"
        >
          <div role="rowgroup">
            <div
              role="row"
              class="grid h-8 grid-cols-template gap-6 border-b border-subtle py-1.5 pl-3 text-base-semibold text-secondary"
            >
              <div role="columnheader">{t(resourceTranslations().membersColumnTitle)}</div>
              <div role="columnheader">{t(resourceTranslations().accountRoleColumnTitle)}</div>
              <div role="columnheader">{t(resourceTranslations().resourceRoleColumnTitle)}</div>
              <div role="columnheader" class="w-9.5" />
            </div>
          </div>
          <div role="rowgroup">
            <For each={members()}>
              {(member) => (
                <MemberRow
                  member={member}
                  resourceRoleOptions={resourceRoleOptions()}
                  deletePermissionAriaLabel={resourceTranslations().deletePermissionAriaLabel}
                  manageAllLabel={resourceTranslations().manageAllLabel}
                  disabled={isRowPending(member)}
                  onSelectRole={(roleId) => selectRole(member, roleId)}
                />
              )}
            </For>
          </div>
        </div>
      </Show>
    </div>
  );
};

export default ResourcePermissionsContent;
